import {type AlgoStatus, type AlgoTradeAccountInfo} from '../algo';
import {type OrderSide, type Symbol} from '../common';
import {type TraderDbTraderAccount} from '../traderdb';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- API schemas explicitly distinguish JSON null from omitted fields.
type NullValue = null;
// eslint-disable-next-line @typescript-eslint/no-restricted-types -- The endpoint can return either a keyed object or a definition array.
type NonNullObject = object;

export type SingleAlgoFullReport = {

  algoRunId: string;
  algoStatus: string;
  algoType: string;
  startDate: string | Date;
  endDate: string | Date;
  createdDate: string | Date;
  abortedDate?: string | Date | NullValue;
  latestStatusDate?: string | Date | NullValue;
  completedDate?: string;
  lastUpdated: string;
  duration?: string | number | NullValue;
  accountInfo?: AlgoAccountInfo | NullValue;
  algoTrades?: AlgoTrade[] | NullValue;
  algoMetrics?: AlgoMetrics | NullValue;
  algoParametersDefinitions?: AlgoReportParameters | NullValue;
  basketValue?: {grossNotionalValue?: number | NullValue; netNotionalValue?: number | NullValue} | NullValue;
  algoParameters?: Record<string, string | number | boolean | NullValue | undefined> | Array<Record<string, unknown>>;
  executionMetrics?: AlgoExecutionMetrics | NullValue;
  symbolsWithQTY?: string[];
  symbols?: string[] | Symbol[];
  cfiCode?: string;
};

type AlgoSubscriptionOverriddenField = 'algoParameters' | 'algoRunId' | 'algoStatus' | 'algoType' | 'createdDate' | 'endDate' | 'lastUpdated' | 'startDate' | 'symbolsWithQTY';

/** One partial, schema-validated algo update after preload status normalization. */
export type AlgoSubscriptionUpdate = Omit<Partial<SingleAlgoFullReport>, AlgoSubscriptionOverriddenField> & {
  algoParameters?: Array<{
    algoParameterId?: string | NullValue;
    doubleValue?: number | NullValue;
    intValue?: number | NullValue;
    selectedValues?: string | NullValue;
    stringValue?: string | NullValue;
  }> | NullValue;
  algoRunId?: string | NullValue;
  algoStatus?: keyof typeof AlgoStatus;
  algoType?: string | NullValue;
  createdDate?: string | Date | NullValue;
  endDate?: string | Date | NullValue;
  lastUpdated?: string | NullValue;
  startDate?: string | Date | NullValue;
  symbolsWithQTY?: string[] | NullValue;
};

/**
 * Algo monitoring list records returned by fetchAlgosList before applications add local grid fields.
 * The list endpoint can omit trade details, so this contract keeps algoTrades optional instead of
 * forcing consumers to pretend the payload is always a full detail report.
 */
export type AlgoMonitorReport = Omit<SingleAlgoFullReport, 'algoStatus' | 'algoTrades'> & {
  algoStatus: keyof typeof AlgoStatus | AlgoStatus;
  algoTrades?: AlgoTrade[] | NullValue;
};

export type AlgoAccountInfo = {
  traderId?: string | NullValue;
  clearingAccount?: string | NullValue;
  executionCode?: string | NullValue;
  internalERPAccount?: string | NullValue;
  customText1?: string | NullValue;
  customText2?: string | NullValue;
  customText3?: string | NullValue;
  memo?: string | NullValue;
  traderInfo?: TraderDbTraderAccount;
};

export type AlgoTrade = {
  symbol?: AlgoTradeSymbol;
  side?: OrderSide | string | number | NullValue;
  algoTradeAutoId?: number | NullValue;
  algoRunAutoId?: number | NullValue;
  qepSymbol?: string | NullValue;
  segmentMic?: string | NullValue;
  algoTradeStatus?: string | NullValue;
  algoTradeDetails?: string | NullValue;
  targetQuantity?: string | number | NullValue;
  tradedQuantity?: string | number | NullValue;
  remainingQuantity?: string | number | NullValue;
  averageFillPrice?: string | number | NullValue;
  benchmarkPrice?: string | number | NullValue;
  launchPrice?: string | number | NullValue;
  deltaBenchmark?: string | number | NullValue;
  gammaBenchmark?: string | number | NullValue;
  vegaBenchmark?: string | number | NullValue;
  thetaBenchmark?: string | number | NullValue;
  fittedVolBenchmark?: string | number | NullValue;
  runningCost?: string | number | NullValue;
  slippage?: string | number | NullValue;
  brokerCode?: string | NullValue;
  createdTime?: string;
  updatedTime?: string | Date | NullValue;
  currencyISOCode?: string | NullValue;
  bidPrice?: string | number | NullValue;
  askPrice?: string | number | NullValue;
  lastMktTradePrice?: string | number | NullValue;
  lastAlgoTradePrice?: string | number | NullValue;
  precentTimePassed?: number;
  percentComplete?: string | number | NullValue;
  perecentAheadOrBehind?: string | number | NullValue;
  midPriceAtTradeCompletion?: string | number | NullValue;
  midPriceAtEndOfAlgo?: string | number | NullValue;
  movedMid?: string | number | NullValue;
  movedBidAsk?: string | number | NullValue;
  account?: AlgoTradeAccountInfo | NullValue;
  accountInfo?: AlgoTradeAccountInfo;
};

/** Product fields exposed by algo reports, including nullable values accepted by the runtime schema. */
export type AlgoTradeSymbol = Pick<Symbol, 'symbol' | 'segmentMic'> & {
  contractSize?: string | number | NullValue;
  currency?: string | NullValue;
  decimalPlaces?: number | NullValue;
  exchangeSymbol?: string | NullValue;
  rootSymbol?: string | NullValue;
  tickSize?: string | number | NullValue;
};

export type AlgoMetrics = {
  numWorkingOrdersOverall?: number | NullValue;
  tradedQuantityOverall?: string | number | NullValue;
  targetQuantityOverall?: string | number | NullValue;
  percentCompleteGrossDelta?: string | number | NullValue;
};

/** Currency metrics returned by the algo detail and subscription schemas. */
export type AlgoExecutionMetrics = {
  runningCost: {
    baseValue?: number | NullValue;
    dollarsValue?: number | NullValue;
    localValue?: string | number | NullValue;
  };
  tradedSlippage?: {dollarsValue?: number | NullValue} | NullValue;
};

export type AlgoReportParameters = NonNullObject;
